import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { ColourChip } from '@/features/common/ui';
import { categoryLabel } from '@/features/quick-add/quick-add';

export interface TagOption {
  id: string;
  name: string;
  colour: string;
}

export interface ProjectOption {
  id: string;
  name: string;
  customerId: string | null;
  customerName: string | null;
  isInternal: boolean;
  status: string;
}

export interface IncomeCategoryOption {
  id: string;
  name: string;
  colour: string;
  emoji?: string | null;
}

/** The org's tags, cached under one key so every picker shares one fetch. */
export function useTags() {
  const org = useActiveOrg();
  return useQuery({
    queryKey: ['tags', org?.id, false],
    enabled: !!org?.id,
    queryFn: () => api.get<{ items: TagOption[] }>('/tags'),
  });
}

export function useProjects() {
  const org = useActiveOrg();
  const perms = usePermissions();
  return useQuery({
    queryKey: ['projects', org?.id, 'all'],
    // A member without project access gets no picker rather than a failed
    // request rendered as an empty one.
    enabled: !!org?.id && perms.can('project', 'view'),
    queryFn: () => api.get<{ items: ProjectOption[] }>(`/projects${qs({ status: 'all' })}`),
  });
}

export function useIncomeCategories() {
  const org = useActiveOrg();
  return useQuery({
    queryKey: ['income-categories', org?.id],
    enabled: !!org?.id,
    queryFn: () => api.get<{ items: IncomeCategoryOption[] }>('/income-categories'),
  });
}

/**
 * Put a record on a project, or take it off.
 *
 * ── WHY COMPLETED PROJECTS ARE FILTERED OUT, EXCEPT THE CURRENT ONE ──
 * Offering every project a business has ever run makes the list unusable within
 * a year. But hiding a completed project that a record is ALREADY on would make
 * the field render empty, and the next save would silently take the record off
 * it - so the current value is always present whatever its status.
 */
export function ProjectSelect({
  value,
  onChange,
  customerId,
  id = 'project',
  disabled,
  hint,
}: {
  value: string;
  onChange: (projectId: string) => void;
  /** When set, only this customer's projects are offered - see `ProjectsService`. */
  customerId?: string;
  id?: string;
  disabled?: boolean;
  hint?: string;
}) {
  const projects = useProjects();

  const options = useMemo(() => {
    const all = projects.data?.items ?? [];
    return all.filter((project) => {
      if (project.id === value) return true;
      if (project.status === 'completed' || project.status === 'cancelled') return false;
      if (customerId === undefined) return true;
      // An invoice may only go on its own customer's project, and an internal
      // project may hold no document at all. The API refuses both; the picker
      // not offering them is what stops somebody meeting that refusal.
      return project.customerId === customerId;
    });
  }, [projects.data, value, customerId]);

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        Project
      </label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm disabled:opacity-50"
      >
        <option value="">Not on a project</option>
        {options.map((project) => (
          <option key={project.id} value={project.id}>
            {project.isInternal
              ? `${project.name} (internal)`
              : `${project.customerName ?? 'No customer'} — ${project.name}`}
          </option>
        ))}
      </select>
      {hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Which kind of revenue an invoice is. Drives the income breakdown. */
export function IncomeCategorySelect({
  value,
  onChange,
  id = 'income-category',
}: {
  value: string;
  onChange: (categoryId: string) => void;
  id?: string;
}) {
  const categories = useIncomeCategories();

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        Income category
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={`${id}-hint`}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
      >
        <option value="">Not classified</option>
        {(categories.data?.items ?? []).map((category) => (
          <option key={category.id} value={category.id}>
            {categoryLabel(category.name, category.emoji)}
          </option>
        ))}
      </select>
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        What kind of work this was. It is what your dashboard breaks your income down by.
      </p>
    </div>
  );
}

/**
 * The tag picker: a text input that filters, plus the chips already applied.
 *
 * Deliberately NOT the full ARIA combobox with `aria-activedescendant` and a
 * managed listbox - the options are rendered as ordinary buttons below the
 * input, which is a listbox pattern a screen reader already understands and
 * which needs no roving focus to be operable. The applied set is announced
 * through a polite live region, because adding a chip changes something far
 * from the control that changed it.
 */
export function TagPicker({
  value,
  onChange,
  onBusyChange,
  id = 'tags',
}: {
  value: TagOption[];
  onChange: (tags: TagOption[]) => void;
  /**
   * Called while a tag is being CREATED, so the form around this control can
   * refuse to submit until it lands.
   *
   * ── THE DEFECT THIS EXISTS FOR ──
   * A newly created tag only reaches `value` in the create mutation's
   * `onSuccess`. Nothing used to stop the surrounding form being submitted
   * while that POST was still in flight - and if it was, `value` was still the
   * old set, so the save wrote `tagIds` WITHOUT the tag the user had just
   * typed, reported success, and closed. The tag was not applied slowly; it was
   * not applied at all, and nothing said so.
   *
   * It survived review because the window is a few tens of milliseconds on an
   * idle machine: type, press Create, press Save, and the create almost always
   * wins. Under load it does not, which is how a full test suite found it and
   * the same test passing alone did not.
   */
  onBusyChange?: (busy: boolean) => void;
  id?: string;
}) {
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const org = useActiveOrg();
  const tags = useTags();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const MAX = 10;
  const applied = new Set(value.map((tag) => tag.id));
  const matches = (tags.data?.items ?? [])
    .filter((tag) => !applied.has(tag.id))
    .filter((tag) => tag.name.toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 6);

  const exact = (tags.data?.items ?? []).some(
    (tag) => tag.name.toLowerCase() === query.trim().toLowerCase(),
  );

  const create = useMutation({
    mutationFn: (name: string) => api.post<TagOption>('/tags', { name }),
    onSuccess: (tag) => {
      onChange([...value, tag]);
      setQuery('');
      void queryClient.invalidateQueries({ queryKey: ['tags', org?.id] });
    },
  });

  // Reported through an effect rather than during render, because the parent
  // sets state from it and doing that mid-render is the other way to make this
  // component the reason a page loops.
  const busy = create.isPending;
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const add = (tag: TagOption) => {
    if (value.length >= MAX) return;
    onChange([...value, tag]);
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        Tags
      </label>

      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <ColourChip
              key={tag.id}
              colour={tag.colour}
              name={tag.name}
              onRemove={() => onChange(value.filter((t) => t.id !== tag.id))}
            />
          ))}
        </div>
      ) : null}

      <input
        ref={inputRef}
        id={id}
        value={query}
        disabled={value.length >= MAX}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Backspace' && query === '' && value.length > 0) {
            onChange(value.slice(0, -1));
            return;
          }
          if (event.key !== 'Enter') return;
          // Enter inside a picker must not submit the form it sits in.
          event.preventDefault();
          const first = matches[0];
          if (first) add(first);
          else if (query.trim() && !exact && perms.can('tag', 'create')) create.mutate(query.trim());
        }}
        placeholder={value.length >= MAX ? 'You can put ten tags on a record' : 'Add a tag…'}
        aria-describedby={`${id}-hint`}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm disabled:opacity-50"
      />

      {query.trim() ? (
        <div className="flex flex-wrap gap-1.5">
          {matches.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => add(tag)}
              className="rounded-full border border-border px-2 py-0.5 text-xs hover:bg-muted"
            >
              {tag.name}
            </button>
          ))}
          {!exact && perms.can('tag', 'create') ? (
            <button
              type="button"
              onClick={() => create.mutate(query.trim())}
              disabled={create.isPending}
              className="rounded-full border border-dashed border-border px-2 py-0.5 text-xs hover:bg-muted"
            >
              Create &ldquo;{query.trim()}&rdquo;
            </button>
          ) : null}
          {matches.length === 0 && !perms.can('tag', 'create') ? (
            <span className="text-xs text-muted-foreground">
              No tag matches. An administrator can add them in Settings.
            </span>
          ) : null}
        </div>
      ) : null}

      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        Anything else worth being able to find later. A record can carry up to ten - they filter, they do
        not add up.
      </p>
      {/* Far from the input that changed it, so it is announced rather than
          left to be discovered. */}
      <span aria-live="polite" className="sr-only">
        {value.length === 0 ? 'No tags' : `${value.map((t) => t.name).join(', ')}. ${value.length} of ${MAX}.`}
      </span>
    </div>
  );
}
